/**
 * AppSidebar — Phase 1 redesign.
 *
 * Renders exactly 7 top-level sections plus a footer area for the
 * organisation switcher and role badge. Section names are text-only — no
 * decorative icons. Active section is computed from the current URL via
 * findActiveSection so any legacy route still highlights the right section.
 *
 * Tier and per-feature gating is no longer enforced in the sidebar; that
 * responsibility now lives at the section / page level (SectionSubNav handles
 * tier-based pill locking, FeatureRouteGuard handles route-level locking).
 *
 * Role-based hiding is intentionally coarse: only org_admin / coach /
 * performance / medical / super_admin see all 7 sections. Every other role
 * sees just Squad and Club; their inner pages are still gated individually.
 */

import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Users,
  Trophy,
  Dumbbell,
  Activity,
  Heart,
  Shield,
  BarChart3,
  Building2,
  Briefcase,
  Settings as SettingsIcon,
  LucideIcon,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useCapability } from "@/hooks/useCapability";
import { useOrganization } from "@/contexts/OrganizationContext";
import { OrganizationSwitcher } from "@/components/organization/OrganizationSwitcher";
import { RoleBadge } from "@/components/auth/RoleBadge";
import { SidebarSkeleton } from "@/components/skeletons/SidebarSkeleton";
import { useSubscription } from "@/hooks/useSubscription";
import { findActiveSection, SECTIONS, SectionDef, SectionKey } from "@/lib/navigation/sections";
import { ROUTES } from '@/lib/routes';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";

/** Roles that see the full nav. */
const FULL_NAV_ROLES = new Set([
  "super_admin",
  "org_admin",
  "coach",
  "performance",
  "medical",
]);

/** Reduced section set for non-staff roles (player_viewer, parent, etc). */
const LIMITED_SECTIONS: SectionKey[] = ["squad"];

const SECTION_ICONS: Record<SectionKey, LucideIcon> = {
  workflows: BarChart3,
  squad: Users,
  performance: Activity,
  medical: Heart,
  admin: SettingsIcon,
};

export function AppSidebar() {
  const location = useLocation();
  const { currentOrganization } = useOrganization();
  const {
    isSuperAdmin,
    canManageOrg,
    role,
    isLoading: roleLoading,
  } = useCapability();
  const { loading: subscriptionLoading, isFeatureEnabled } = useSubscription();

  // Inbox badge removed — club_registrations and sponsorship_enquiries tables dropped.

  const isReady = !roleLoading && !subscriptionLoading;

  // Decide which sections to show based on role + per-section feature/role gates.
  const visibleSections: SectionDef[] = (() => {
    if (!role && !isSuperAdmin) return [];
    const allowFull = isSuperAdmin || (role && FULL_NAV_ROLES.has(role));
    const base = allowFull
      ? SECTIONS
      : SECTIONS.filter((s) => LIMITED_SECTIONS.includes(s.key));
    return base.filter((section) => {
      // Super admin bypasses all section-level gates.
      if (isSuperAdmin) return true;
      if (section.feature && !isFeatureEnabled(section.feature)) return false;
      if (section.roles && section.roles.length > 0) {
        if (!role || !section.roles.includes(role)) return false;
      }
      return true;
    });
  })();

  const activeSection = findActiveSection(location.pathname);

  return (
    <Sidebar
      collapsible="offcanvas"
      className={cn(
        // Apply a soft gradient wash to the inner sidebar surface (the element
        // shadcn hardcodes `bg-sidebar` on). `background-image` and
        // `background-color` don't collide, so the gradient layers on top.
        "[&_[data-sidebar=sidebar]]:bg-gradient-to-b",
        "[&_[data-sidebar=sidebar]]:from-sidebar",
        "[&_[data-sidebar=sidebar]]:via-sidebar",
        "[&_[data-sidebar=sidebar]]:to-card-elevated/40",
      )}
    >
      <SidebarHeader className="bg-gradient-to-br from-primary/5 via-transparent to-transparent">
        <Link
          to={ROUTES.DASHBOARD}
          className="flex items-center px-3 py-3 hover:opacity-80 transition-opacity"
        >
          <span className="font-bold text-lg tracking-tight">AlphaSignal</span>
        </Link>
        <div className="px-2">
          <OrganizationSwitcher />
        </div>
        <div className="px-3 pb-1">
          <RoleBadge />
        </div>
      </SidebarHeader>

      <SidebarContent>
        {!isReady ? (
          <SidebarSkeleton />
        ) : (
          <SidebarMenu className="px-2 gap-0.5">
            {visibleSections.map((section) => {
              const isActive = activeSection?.key === section.key;
              const showBadge = false;
              const Icon = SECTION_ICONS[section.key];

              return (
                <SidebarMenuItem key={section.key}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive}
                    className={cn(
                      "h-auto px-3 py-2.5 text-[15px] font-medium tracking-tight rounded-md",
                      "data-[active=true]:bg-gradient-to-r",
                      "data-[active=true]:from-primary/15",
                      "data-[active=true]:via-primary/10",
                      "data-[active=true]:to-primary/5",
                      "data-[active=true]:text-primary",
                      "data-[active=true]:font-semibold",
                      "data-[active=true]:border-l-2",
                      "data-[active=true]:border-primary",
                    )}
                  >
                    <Link to={section.basePath} className="flex items-center justify-between w-full">
                      <span className="flex items-center gap-2">
                        {Icon && <Icon className="h-4 w-4 shrink-0" />}
                        <span>{section.label}</span>
                      </span>
                      {showBadge && null}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        )}
      </SidebarContent>

      <SidebarFooter className="bg-card-elevated/30 border-t border-border/30">
        <div className="px-3 py-2 text-xs text-muted-foreground tracking-tight">
          {currentOrganization?.name ?? ""}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
